/* eslint-disable no-console */
import { vestResolver } from '@hookform/resolvers/vest'
import { Button, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import selectOptions from '../../../constants/selectOptions'
import { IUserProfile } from '../../../pages/UsersPage/constants'
import { AppDispatch } from '../../../store'
import authSelectors from '../../../store/auth/authSelectors'
import reportsSelectors from '../../../store/reports/reportsSelectors'
import {
  deselectCase,
  updateReportTriage,
  getRequiresTriage,
  getUsersReports,
  getAdminReports,
} from '../../../store/reports/reportsSlice'
import { triageLevelStates } from '../../../store/reports/types'
import { userRoles } from '../../../store/auth/userTypes'
import CardTAG from '../../CardTAG'
import DividerTAG from '../../DividerTAG'
import Dropdown from '../../Dropdown'
import ImmediateItentionForm from '../../forms/ImmediateItentionForm'
import ResolvedWithReportingPartyForm from '../../forms/ResolvedWithReportingPartyForm'
import constants, { messages } from './constants'
import { formTypes, IReportTriageLevelForm } from './types'
import { formValidation } from './utils'
import { APP_MODE } from '../../../sharedTypes'
import { useNavigate } from 'react-router-dom';

import { success } from '../../../utils/alertUtils';

const ReportTriageLevel: React.FC = () => {
  const [searchUser, setSearchUser] = useState<string>('')
  const selectedReport = useSelector(reportsSelectors.selectedReport)
  const appMode = useSelector(authSelectors.userOrganizationType)
  const authRoles = useSelector(authSelectors.userRoles)

  const formMethods = useForm<IReportTriageLevelForm>({
    defaultValues: constants.initialDefaultValues,
    resolver: vestResolver(formValidation),
    mode: 'onChange',
    shouldFocusError: false,
    shouldUnregister: false,
  })

  const { register, handleSubmit, formState, control, watch, setValue, reset } = formMethods
  // useEffect(() => {
  //   setValue('triageLevel', selectedReport?.triage_level)
  // }, [selectedReport])
  useEffect(() => {
      setValue('triageLevel', selectedReport?.triage_level ?? '')
    }, [selectedReport])

  const resolveMessage = () => messages(triageLevelWatch)

  const resolveForm = (level: string) => forms[level]

  const onSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchUser(e.target.value)
  }

  const handleUsersSelect = (data: IUserProfile) => {
    const indexOfObject = selectedUsersWatch?.findIndex(x => x.user.id === data.user.id)

    if (selectedUsersWatch?.length !== 0 && indexOfObject !== -1) {
      const reducedArray = [...selectedUsersWatch]
      reducedArray.splice(indexOfObject, 1)
      setValue('investigator', reducedArray)
      return
    }
    setValue('investigator', [...selectedUsersWatch, data])
  }

  const handleUserRemove = (userId: number) => {
    const arrCopy = [...selectedUsersWatch]
    const objWithIdIndex = arrCopy.findIndex(obj => obj.user.id === userId)
    arrCopy.splice(objWithIdIndex, 1)
    setValue('investigator', arrCopy)
  }

  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate();
  const handleConfirm = async (data: IReportTriageLevelForm) => {
    let updateResult
    if (
      triageLevelWatch === triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED ||
      triageLevelWatch === triageLevelStates.TRANSFERED_FOR_ASSIGNMENT
    ) {
      const arrayOfUsers = data?.investigator as IUserProfile[]
      const dataBody = {
        message: data?.message,
        investigator: arrayOfUsers[0]?.id,
      }
      updateResult = await dispatch(
        updateReportTriage({
          type: triageLevelWatch,
          dataBody,
          selectedReportId: selectedReport?.id as number,
        }),
      )
    }

    if (triageLevelWatch === triageLevelStates.ACCEPTED_AND_ASSIGNED) {
      updateResult = await dispatch(
        updateReportTriage({
          type: triageLevelWatch,
          dataBody: {},
          selectedReportId: selectedReport?.id as number,
        }),
      )
    }

    if (
      triageLevelWatch === triageLevelStates.ADDRESSED_AND_RESOLVED ||
      triageLevelWatch === triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED ||
      triageLevelWatch === triageLevelStates.RESOLVE_AND_ARCHIVE
    ) {
      const dataBody =
        triageLevelWatch === triageLevelStates.ADDRESSED_AND_RESOLVED ||
        triageLevelWatch === triageLevelStates.RESOLVE_AND_ARCHIVE
          ? {
              triage_level: triageLevelStates.ADDRESSED_AND_RESOLVED,
            }
          : {
              triage_level: triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED,
            }

      updateResult = await dispatch(
        updateReportTriage({
          type:
            triageLevelWatch === triageLevelStates.RESOLVE_AND_ARCHIVE
              ? triageLevelStates.ADDRESSED_AND_RESOLVED
              : triageLevelWatch,
          dataBody,
          selectedReportId: selectedReport?.id as number,
        }),
      )
    }

    // If the update failed the thunk has already shown an error toast. Keep the
    // case where it is: no success message, no refetch, no navigation.
    if (updateResult && updateReportTriage.rejected.match(updateResult)) {
      return
    }

    // Refetch inbox data so the case immediately moves into the correct
    // grouping (e.g. "Under Investigation") without a manual page refresh.
    if (authRoles?.includes(userRoles.ADMIN)) {
      dispatch(getAdminReports())
    } else {
      dispatch(getUsersReports())
    }
    if (authRoles?.includes(userRoles.DESIGNATED_RECEIVER)) {
      dispatch(getRequiresTriage())
    }

    dispatch(deselectCase({ value: undefined }))
    success('Report triage level updated')
    navigate('/cases/inbox')
  }

  // #region form flow

  const triageLevelWatch = watch('triageLevel') as string
  const selectedUsersWatch = watch('investigator') as IUserProfile[]

  const getMessage = useCallback(
    (field: formTypes) => {
      if (!formState.errors[field]) return ''

      return formState.errors[field]!.message
    },
    [formState],
  )

  useEffect(() => {
    reloadDefaultValues(constants.defaultValuesForms[triageLevelWatch])
  }, [triageLevelWatch])

  const reloadDefaultValues = useCallback(
    (values: IReportTriageLevelForm) => {
      reset(values)
    },
    [reset],
  )

  // #endregion

  const forms: { [key: string]: any } = {
    [triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED]: (
      <ImmediateItentionForm
        searchValue={searchUser}
        onSearchValueChange={onSearchValueChange}
        handleSelect={handleUsersSelect}
        selectedUsers={selectedUsersWatch}
        removeUser={handleUserRemove}
        register={register}
        messageError={getMessage('userMessage')}
        selectedUsersError={getMessage('selectedUsers')}
      />
    ),
    [triageLevelStates.ADDRESSED_AND_RESOLVED]: !APP_MODE.light && (
      <ResolvedWithReportingPartyForm
        register={register}
        errorDescription={getMessage('issueDescription')}
        errorResponse={getMessage('response')}
      />
    ),
    [triageLevelStates.TRANSFERED_FOR_ASSIGNMENT]: (
      <ImmediateItentionForm
        searchValue={searchUser}
        onSearchValueChange={onSearchValueChange}
        handleSelect={handleUsersSelect}
        selectedUsers={selectedUsersWatch}
        removeUser={handleUserRemove}
        register={register}
        messageError={getMessage('userMessage')}
        selectedUsersError={getMessage('selectedUsers')}
      />
    ),
    [triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED]: <></>,
    [triageLevelStates.ACCEPTED_AND_ASSIGNED]: <></>,
  }

  const dropdownOptions =
    appMode === APP_MODE.full ? selectOptions.triageLevels : selectOptions.triageLevelsLight
  return (
    <form onSubmit={handleSubmit(handleConfirm)}>
      <CardTAG
        className={triageLevelWatch ? triageLevelWatch : 't-empty'}
        title='Report Triage (Required)'
        boxError={triageLevelWatch === triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED}
      >
        <div className='t-report-card-top'>
          {/* <Dropdown
            control={control}
            fieldName='triageLevel'
            label='Select A Triage Level'
            selectedValue={triageLevelWatch}
            options={dropdownOptions}
            error={getMessage('triageLevel')}
            placeholder='No Triage Level Assigned Yet'
          /> */}
          <Dropdown
            control={control}
            fieldName='triageLevel'
            label='Select A Triage Level'
            selectedValue={triageLevelWatch ?? ''} // ✅ fallback to empty string
            options={dropdownOptions}
            error={getMessage('triageLevel')}
            placeholder='No Triage Level Assigned Yet'
          />

          {![
            triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED,
            triageLevelStates.ACCEPTED_AND_ASSIGNED,
          ].includes(triageLevelWatch as any) && resolveMessage()}
        </div>
        {triageLevelWatch === 'IMMEDIATE_ATTENTION_REQUIRED' && resolveMessage()}
        {triageLevelWatch && <DividerTAG />}
        {resolveForm(triageLevelWatch)}
        <Button
          variant='outlined'
          sx={{ borderRadius: '1rem', border: '1px solid black !important' }}
          type='submit'
          disabled={!triageLevelWatch}
        >
          <Typography variant='bodyRegular' sx={{ textTransform: 'none' }}>
            Confirm
          </Typography>
        </Button>
      </CardTAG>
    </form>
  )
}

export default ReportTriageLevel
